import { useEffect, useState } from 'react';
import { ArrowTopRightOnSquareIcon, ChatBubbleLeftRightIcon, LinkIcon } from '@heroicons/react/24/outline';
import MainStore from '../MainStore';
import { buildSourceChain } from '../utils/sourceChainWalker';

function Quote({ text }){
    return <span className="text-sm text-slate-600 italic leading-snug">"{text}"</span>
}

function SourceRow({ primitive, id, depth = 0 }){
    const mainstore = MainStore();
    const title = primitive?.title ?? primitive?.name ?? `Source #${id}`;
    const plainId = primitive?.plainId;
    const sourceUrl = primitive?.referenceParameters?.url ?? primitive?.url ?? primitive?.metadata?.url;

    return (
        <div className="flex items-center gap-2 min-w-0 pb-1">
            {sourceUrl && (
                <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="shrink-0 text-slate-400 hover:text-blue-600" onClick={e => e.stopPropagation()} title="Open source">
                    <LinkIcon className="h-3.5 w-3.5" />
                </a>
            )}
            <div className="flex-1 min-w-0 w-0">
                <p className="text-xs text-slate-400 truncate">{title}</p>
                {plainId && <p className="text-[10px] text-slate-400 truncate">#{plainId}</p>}
            </div>
            <button
                type="button"
                className="flex items-center justify-center h-6 w-6 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 shrink-0"
                title="Open in sidebar"
                onClick={() => mainstore.sidebarSelect?.(primitive)}>
                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
            </button>
        </div>
    );
}

function SourceChain({ evidenceId, chain }){
    if (!chain || chain.length === 0) {
        return (
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <div className="px-3 py-2 text-[12px] text-slate-400">Source #{evidenceId} not found</div>
            </div>
        );
    }

    return (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            {chain.map(({ prim, quotes, depth }, i) => {
                if (!prim) return null;
                const pad = depth * 16;
                const isTop = depth === 0;
                return (
                    <div key={prim.id ?? i} className="mb-2">
                        <div style={{ paddingLeft: `${pad}px` }}>
                            {quotes.length > 0 && (
                                <p className={`text-sm text-slate-600 italic leading-snug break-words ${isTop ? '' : 'border-l-2 border-slate-300 pl-2'}`}>
                                    {quotes.map((q, qi)=><span key={qi}>"{q}"</span>)}
                                </p>
                            )}
                            <div className={isTop ? '' : 'border-l-2 border-slate-300 pl-2'}>
                                <SourceRow primitive={prim} id={prim.id} depth={depth} />
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export function CitationReferenceInfo({ ids = [], quotes, id_map, summaryId, ...props }){
    const mainstore = MainStore()
    const normIds = (Array.isArray(ids) ? ids : []).map((d)=>`${d}`.trim()).filter(Boolean)
    const rawQuotes = Array.isArray(quotes) ? quotes : (quotes != null ? [quotes] : [])
    const hasMap = id_map && typeof id_map === "object" && !Array.isArray(id_map) && Object.keys(id_map).length > 0

    const usedQuoteIdx = new Set()
    const cleanQuote = (q)=>(typeof q === "string" ? q.trim() : "")

    const quotesForId = (id, index)=>{
        const out = []
        if( hasMap ){
            const idxs = id_map[id] ?? id_map[`${id}`]
            if( Array.isArray(idxs) ){
                idxs.forEach((i)=>{
                    const q = cleanQuote(rawQuotes[i])
                    if( q ){ out.push(q); usedQuoteIdx.add(i) }
                })
            }
        }else if( normIds.length === rawQuotes.length ){
            const q = cleanQuote(rawQuotes[index])
            if( q ){ out.push(q); usedQuoteIdx.add(index) }
        }
        return out
    }

    const [retry, setRetry] = useState(0);

    const sources = normIds.map((id, index)=> ({
        id,
        ...buildSourceChain({
            evidenceId: id,
            quoteForEvidence: quotesForId(id, index),
            summaryId
        })
    }))

    // Collect all missing IDs and fetch them.  When they resolve the store
    // updates; bump retry to trigger a re-render that rebuilds the chains.
    useEffect(()=>{
        const allMissing = [...new Set(sources.flatMap(s => s.missingIds || []))];
        if (allMissing.length === 0) return;
        const mainstore = MainStore();
        Promise.all(allMissing.map(id => mainstore.fetchPrimitive?.(id))).then(()=>{
            setRetry(c => c + 1);
        });
    }, [retry, sources.map(s => s.missingIds?.join(',')).join('|')]);

    const orphanQuotes = rawQuotes
        .map((q, i)=>({ q: cleanQuote(q), i }))
        .filter(({ q, i })=>q && !usedQuoteIdx.has(i))
        .map(({ q })=>q)

    return <div className="w-full max-w-[min(32rem,calc(100vw-4rem))] py-1">
        <div className="flex items-center gap-1.5 px-3 pb-1.5 text-slate-500">
            <ChatBubbleLeftRightIcon className="h-3.5 w-3.5"/>
            <p className="text-[11px] font-semibold uppercase tracking-wide">
                {sources.length || rawQuotes.length} {(sources.length || rawQuotes.length) === 1 ? "source" : "sources"}
            </p>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-2 pb-1 space-y-2">
            {sources.map(({ id, chain })=>(
                <SourceChain key={id} evidenceId={id} chain={chain} />
            ))}
            {orphanQuotes.length > 0 && <div className="flex flex-col gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Quotes</p>
                {orphanQuotes.map((q, qi)=><Quote key={qi} text={q}/>)}
            </div>}
        </div>
    </div>
}
